import { Inject, Injectable } from '@angular/core';
import { ApiCommonService, TimeApiService } from 'cbc-shared-lib';
import { Observable, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import {
  SGKE152AService,
  Sgke152aRequest,
  Sgke152aResponse,
} from 'sg-api';
import { FeaturesLibConfig } from '../../../../../config/features-lib-config';
import { FEATURES_LIB_CONFIG } from '../../../../../config/features-lib-config.token';

/** HTTPヘッダーからファイル名が取得できなかった場合のデフォルトファイル名 */
const DEFAULT_FILENAME = 'SGKE010R_入金報告表.xlsm';

@Injectable({
  providedIn: 'root',
})
export class UsedSGKE152AService {

  /**
   * コンストラクタ
   * @param {Sgke152aService} service
   * @param {ApiCommonService} apiCommonService
   * @param {TimeApiService} timeApiService
   * @param {FeaturesLibConfig} featuresLibConfig
   * @memberof UsedSgke152aService
   */
  constructor(
    public service: SGKE152AService,
    public apiCommonService: ApiCommonService,
    private timeApiService: TimeApiService,
    @Inject(FEATURES_LIB_CONFIG) private featuresLibConfig: FeaturesLibConfig
  ) { }

  /**
   * SGKE152A_入金報告表出力API呼び出し
   * @param {Sgke152aRequest} request
   * @return {*}  {Observable<Blob>}
   * @memberof UsedSgke152aService
   */
  public doSgke152a(request: Sgke152aRequest): Observable<Sgke152aResponse> {
    //日付のフォーマットをYYMMDDに変更
    this.apiCommonService.convertElementInObject(request, Object.prototype.toString.call(new Date()), this.timeApiService.convertDateToString);
    return this.service.sgke152aPost(this.apiCommonService.setRequestCommon(<any>request), 'response')
      .pipe(
        map(
          response => {
            let jsonResponse: Sgke152aResponse;

            // コンテントタイプを取得
            const contentType = response.headers.get('Content-Type');

            if (contentType === 'application/octet-stream') {
              // ファイルが取得できた場合

              const fileBlob = new Blob([response.body], { type: contentType });

              const contentDisposition: string = response.headers.get('Content-Disposition');
              let fileName = this.getFilenameFromContentDisposition(contentDisposition);

              // ファイルとしてダウンロード
              this.anchorDownload(fileBlob, fileName);
              return null;
            } else if (this.service.configuration.isJsonMime(contentType)) {
              // JSONの場合
              const textDecoder = new TextDecoder('utf-8');
              // ArrayBufferを文字列に変換
              const jsonString = textDecoder.decode(response.body);
              // JSONオブジェクトに変換
              jsonResponse = JSON.parse(jsonString);

              const responseClone = response.clone({ body: jsonResponse });

              // エラーが設定されていないかチェックする
              this.apiCommonService.checkError(responseClone);

            }
            return jsonResponse as Sgke152aResponse;
          }),
        timeout(Number(this.featuresLibConfig.httpTimeout)),
        catchError((err) => {
          this.apiCommonService.dispErrorMessage(err, 'bill', 'SGKE152A');
          return throwError(err.message);
        })
      );

  }

  /**
   * 渡されたBlobオブジェクトとファイル名を元に内部でAnchor要素を作成し、
   * ブラウザ上でファイルとしてダウンロードする
   * @param {Blob} response
   * @param {string} filename
   * @memberof UsedSgke152aService
   */
  anchorDownload(response: Blob, filename: string): void {
    // 取得した情報をアンカー要素のURLに指定
    let downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(response);
    // ダウンロード属性を指定して、ファイルとしてダウンロードするように指定
    downloadLink.setAttribute('download', filename);
    document.body.appendChild(downloadLink);
    // アンカー要素をクリックしてダウンロード
    downloadLink.click();
    // 追加した要素を削除
    document.body.removeChild(downloadLink);
  }

  /**
   * HttpヘッダーのContent-Dispositionからファイル名を取得する
   * @param {(string | null)} header
   * @return {*}  {string}
   * @memberof UsedSgke152aService
   */
  getFilenameFromContentDisposition(header: string | null): string {
    if (!header) return DEFAULT_FILENAME;

    // filename="ファイル名" からファイル名を取得する正規表現
    // ※クォートあり・なし両方に対応, セミコロン区切りの複数属性にも対応, 行末でなくてもOK
    const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);

    // 正規表現で抽出したファイル名から クォートを取り除く処理
    return match ? decodeURI(match?.[1].replace(/['"]/g, '')) : DEFAULT_FILENAME;
  }

}
